/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import Antenna from '../../shared/entities/antenna';
import HNAOverride from '../../shared/entities/hnaOverride';
import MissionPlan from '../../shared/entities/missionPlan';
import MissionPlanDetails from '../../shared/entities/missionPlanDetails';
import Terminal from '../../shared/entities/terminal';
import {Action, ActionCreators} from './actions';
import {
  HnaNetwork,
  HnaNetworkApprovalState,
  HnaZone as HnaZoneV2
} from "../../shared/entities/hnaRequest";

export const SESSION_STORAGE_MISSION_PLAN_ID = 'up_missionPlanId';

export enum PlansSortDirection {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}
export enum SortField {
  TITLE = 'title',
  TAIL = 'tail',
  LEGS = 'legs',
  ORIGIN = 'origin',
  ANTENNA = 'antenna',
  HCX = 'hcx',
  DATE = 'date',
}

export const loadMissionPlanIdFromSessionStorage = () => {
  let missionPlanId;

  try {
    const sessionMissionId = sessionStorage.getItem(SESSION_STORAGE_MISSION_PLAN_ID);
    missionPlanId = sessionMissionId !== null ? parseInt(sessionMissionId) : undefined;
  } catch (error) {
    // ignore error and return undefined
  }

  return missionPlanId;
};

export type FlightLegChartData = {
  x: string;
  y: number;
  timeSinceTakeOffMin: string;
  timestamp: string;
  description: string;
  userSetTimestamp?: string;
  color?: string;
  type?: string;
  marginalService?: boolean;
};
export interface State {
  readonly missionPlans: { [id: number]: MissionPlan };
  readonly isRequestingMissionPlans: boolean;
  readonly requestingMissionPlansError?: string;
  readonly plansListSortDirection: PlansSortDirection;
  readonly plansListSortField: SortField;
  readonly savingMissionPlan: boolean;
  readonly missionPlanSavedOrUpdated: boolean;
  readonly savingMissionPlanError?: boolean;
  readonly missionPlanById: MissionPlanDetails;
  readonly requestingMissionPlanById: boolean;
  readonly requestingMissionPlanByIdError?: boolean;
  readonly deletingMissionPlan: boolean;
  readonly deletingMissionPlanSuccess?: boolean;
  readonly deletingMissionPlanError?: boolean;
  readonly selectedAntenna?: Antenna;
  readonly selectedTerminal?: Terminal;
  readonly overrideRecommendedAntenna: boolean;
  readonly missionDetailsLoading: boolean;
  readonly deleteConfirmation: boolean;

  readonly selectedMissionPlanId?: number;
  readonly flightLegVictoryChartData?: { [flightLegId: number]: FlightLegChartData[] | undefined };

  readonly hnaOverrideData: HnaZoneV2[] | undefined;
  readonly isRequestingHnaOverrideData: boolean;
  readonly requestingHnaOverrideDataError?: string;
}

export const initialMissionsState: State = {
  missionPlans: {},
  isRequestingMissionPlans: false,
  requestingMissionPlansError: undefined,
  plansListSortDirection: PlansSortDirection.ASCENDING,
  plansListSortField: SortField.DATE,
  savingMissionPlan: false,
  missionPlanSavedOrUpdated: false,
  savingMissionPlanError: undefined,
  missionPlanById: {
    aircraftTailNum: null,
    aircraftTypeId: -1,
    antennaType: null,
    antennaTypeId: -1,
    creationDate: null,
    handOffBuffer: null,
    id: null,
    landingBuffer: null,
    name: null,
    sedFileVersion: null,
    sscfFileVersion: null,
    takeOffBuffer: null,
    datapathUuid: null,
    useHcxBeams: false,
    useNmsConfiguredMapVersion: false,
    user: null,
    viHnaPolicyVersion: null,
  },
  requestingMissionPlanById: false,
  requestingMissionPlanByIdError: undefined,
  deletingMissionPlan: false,
  deletingMissionPlanError: undefined,
  deletingMissionPlanSuccess: undefined,
  selectedAntenna: undefined,
  selectedTerminal: undefined,
  overrideRecommendedAntenna: true,
  missionDetailsLoading: true,
  deleteConfirmation: false,
  selectedMissionPlanId: loadMissionPlanIdFromSessionStorage(),
  hnaOverrideData: undefined,
  isRequestingHnaOverrideData: false,
  requestingHnaOverrideDataError: undefined,
};

// Reducer
export default function reducer(state: State = initialMissionsState, action: Action): State {
  let partialState: Partial<State> | undefined;
  switch (action.type) {
    case ActionCreators.RequestMissionPlans.type:
      partialState = {
        isRequestingMissionPlans: true,
        requestingMissionPlansError: undefined,
      };
      break;

    case ActionCreators.ReceivedMissionPlansSuccess.type: {
      const missionPlans: MissionPlan[] = action.payload.response.slice();
      const compiledMissionPlans: { [id: number]: MissionPlan } = {};
      missionPlans.forEach((missionPlan: MissionPlan) => {
        compiledMissionPlans[missionPlan.id] = missionPlan;
      });
      partialState = {
        missionPlans: compiledMissionPlans,
        requestingMissionPlansError: undefined,
        isRequestingMissionPlans: false,
      };
      break;
    }
    case ActionCreators.ReceivedMissionPlansError.type:
      partialState = {
        requestingMissionPlansError: action.payload,
        isRequestingMissionPlans: false,
      };
      break;
    case ActionCreators.ClearReceivedMissionPlansError.type:
      partialState = {
        requestingMissionPlansError: undefined,
      };
      break;
    case ActionCreators.SetPlansListSortDirection.type:
      partialState = {
        plansListSortDirection: action.payload,
      };
      break;
    case ActionCreators.SetPlansListSortField.type:
      partialState = {
        plansListSortField: action.payload,
      };
      break;

    case ActionCreators.SaveMissionPlan.type:
      partialState = {
        savingMissionPlan: true,
        savingMissionPlanError: undefined,
      };
      break;

    case ActionCreators.SavedMissionPlanSuccess.type:
      partialState = {
        missionPlanById: { ...action.payload.response },
        savingMissionPlan: false,
        savingMissionPlanError: undefined,
        missionPlanSavedOrUpdated: true,
      };
      break;

    case ActionCreators.ClearSavedMissionPlan.type:
      partialState = {
        missionPlanById: {
          aircraftTailNum: null,
          aircraftTypeId: 0,
          antennaType: null,
          antennaTypeId: 0,
          creationDate: null,
          handOffBuffer: null,
          id: null,
          landingBuffer: null,
          name: null,
          sedFileVersion: null,
          sscfFileVersion: null,
          takeOffBuffer: null,
          datapathUuid: null,
          useHcxBeams: false,
          useNmsConfiguredMapVersion: false,
          user: null,
          viHnaPolicyVersion: null,
        },
      };
      break;

    case ActionCreators.SavedMissionPlanError.type:
      partialState = {
        savingMissionPlanError: action.payload,
        savingMissionPlan: false,
      };
      break;

    case ActionCreators.ClearSavedMissionPlanError.type:
      partialState = {
        savingMissionPlanError: undefined,
      };
      break;

    case ActionCreators.GetMissionPlanById.type:
      partialState = {
        requestingMissionPlanById: true,
        requestingMissionPlanByIdError: undefined,
      };
      break;

    case ActionCreators.ClearSavedMissionPlanById.type:
      partialState = {
        missionPlanById: {
          aircraftTailNum: null,
          aircraftTypeId: 0,
          antennaType: null,
          antennaTypeId: 0,
          creationDate: null,
          handOffBuffer: null,
          id: null,
          landingBuffer: null,
          name: null,
          sedFileVersion: null,
          sscfFileVersion: null,
          takeOffBuffer: null,
          datapathUuid: null,
          useHcxBeams: false,
          useNmsConfiguredMapVersion: false,
          user: null,
          viHnaPolicyVersion: null,
        },
      };
      break;

    case ActionCreators.ReceivedMissionPlanByIdSuccess.type:
      partialState = {
        missionPlanById: { ...action.payload.response },
        requestingMissionPlanById: false,
        savingMissionPlanError: undefined,
      };
      break;

    case ActionCreators.ReceivedMissionPlanByIdError.type:
      partialState = {
        requestingMissionPlanByIdError: action.payload,
        requestingMissionPlanById: false,
      };
      break;

    case ActionCreators.ClearReceivedMissionPlanByIdError.type:
      partialState = {
        requestingMissionPlanByIdError: undefined,
      };
      break;

    case ActionCreators.DeleteMissionPlan.type:
      partialState = {
        deletingMissionPlan: true,
      };
      break;

    case ActionCreators.DeletedMissionPlanSuccess.type:
      partialState = {
        deletingMissionPlan: false,
        deletingMissionPlanError: undefined,
        deletingMissionPlanSuccess: true,
      };
      break;

    case ActionCreators.DeletedMissionPlanError.type:
      partialState = {
        deletingMissionPlanError: action.payload,
        deletingMissionPlan: false,
      };
      break;

    case ActionCreators.ClearDeletedMissionPlanSuccess.type:
      partialState = {
        deletingMissionPlanSuccess: undefined,
      };
      break;

    case ActionCreators.ClearDeletedMissionPlanError.type:
      partialState = {
        deletingMissionPlanError: undefined,
      };
      break;

    case ActionCreators.SetSelectedAntenna.type:
      partialState = {
        selectedAntenna: action.payload,
      };
      break;

    case ActionCreators.ClearSelectedAntenna.type:
      partialState = {
        selectedAntenna: undefined,
      };
      break;

    case ActionCreators.SetSelectedTerminal.type:
      partialState = {
        selectedTerminal: action.payload,
      };
      break;

    case ActionCreators.ClearSelectedTerminal.type:
      partialState = {
        selectedTerminal: undefined,
      };
      break;

    case ActionCreators.SetOverrideRecommendedAntenna.type:
      partialState = {
        overrideRecommendedAntenna: action.payload,
      };
      break;

    case ActionCreators.SetMissionDetailsLoading.type:
      partialState = {
        missionDetailsLoading: action.payload,
      };
      break;

    case ActionCreators.DeleteMissionConfirmation.type:
      partialState = {
        deleteConfirmation: action.payload,
      };
      break;

    case ActionCreators.SetFlightLegVictoryChartData.type:
      return {
        ...state,
        flightLegVictoryChartData: {
          ...state.flightLegVictoryChartData,
          [action.payload.flightLegId]: action.payload.data,
        },
      };

    case ActionCreators.DeleteFlightLegVictoryChartData.type: {
      let chartsData = state.flightLegVictoryChartData;

      if (chartsData && chartsData[action.payload]) {
        chartsData[action.payload] = undefined;
      }

      return {
        ...state,
        flightLegVictoryChartData: {
          ...chartsData,
        },
      };
    }

    case ActionCreators.SetSelectedMissionPlanId.type:
      partialState = {
        selectedMissionPlanId: action.payload,
      };
      break;

    case ActionCreators.ClearSelectedMissionPlanId.type:
      partialState = {
        selectedMissionPlanId: undefined,
      };
      break;

    case ActionCreators.RequestHnaOverrideData.type:
      partialState = {
        isRequestingHnaOverrideData: true,
        requestingHnaOverrideDataError: undefined,
      };
      break;
    case ActionCreators.ReceivedHnaOverrideDataSuccess.type: {
      const hnaOverrideData: HNAOverride = action.payload.response;

      // Convert the HNAOverrideNetworkInformation to HnaNetworkType[]
      // This allows us to reuse the V1 endpoint data to populate the
      // initial state of the V2 form. When more networks are added
      // we'll need to make a small refactor to this.
      const v1ToV2NetworkMap = {
        "hcxGround": "HCX Ground",
        "hcxOverflight": "HCX Overflight",
        "alOverflight": "AL Overflight",
        "alGround": "AL Ground",
        "viOverflight": "VI Overflight",
        "viGround": "VI Ground",
      }
      const hnaZonesOut: HnaZoneV2[] = [];
      for (const {country, networkInformation} of hnaOverrideData.hnaZones) {
        const networks: HnaNetwork[] = [];

        Object.entries(networkInformation).forEach(([network, isNetworkRequested]) => {
          if (!isNetworkRequested) return;

          networks.push({
            network: v1ToV2NetworkMap[network],
            state: HnaNetworkApprovalState.REQUESTED,
          });
        });

        hnaZonesOut.push({
          country,
          networks,
        });
      }

      partialState = {
        hnaOverrideData: hnaZonesOut,
        requestingHnaOverrideDataError: undefined,
        isRequestingHnaOverrideData: false,
      };
      break;
    }

    case ActionCreators.ClearHnaOverrideData.type:
      partialState = {
        hnaOverrideData: undefined,
        requestingHnaOverrideDataError: undefined,
        isRequestingHnaOverrideData: false,
      };
      break;
    case ActionCreators.ReceivedHnaOverrideDataError.type:
      partialState = {
        requestingHnaOverrideDataError: action.payload,
        isRequestingHnaOverrideData: false,
      };
      break;
    case ActionCreators.SetMissionPlanSavedOrUpdated.type:
      partialState = {
        missionPlanSavedOrUpdated: action.payload,
      };
      break;
    default:
      return state;
  }
  return { ...state, ...partialState };
}
